import React, { type JSX } from 'react';
import query from './AnalysutdelningQuery.gql';
import type {
  AnalysutdelningQuery as IData,
  AnalysutdelningQueryVariables as IVariables,
  AnalysutdelningQuery_db_EKEkonomirapport_All__Associations as IRapportAssociations,
  AnalysutdelningQuery_db_EKEkonomirapport_All__Associations_Utdelade__Associations as IUtdeladAssociations,
  AnalysutdelningQuery_db_EKEkonomirapport_All,
  AnalysutdelningQuery_ek_grundurval,
  AnalysutdelningQuery_gbsroles as IGBSRole,
} from './_QueryTypes/AnalysutdelningQuery';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../Utils/mergeSort';
import { TEDEKRapportDimensionTypEnum } from '../../../../../../../globalQueryTypes';
import useHandledQuery from '../../../../../../../Components/QueryComponents/HandledQuery/HandledQueryHook';
import { kodplaner_ekkodplaner } from '../../../../../../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import { getGbsRoles } from '../../Components/AnalysUtdelningTable/Utils/AnalysUtdelningTableUtils';

interface IProps {
  activeDatabaseName: string;
  activeKodplan: kodplaner_ekkodplaner;
  children: (results: IChildArgs) => JSX.Element;
}

export interface IRapportUtdelning {
  id: string;
  name: string;
  availableForAnalys: boolean;
  defaultForNewUser: boolean;
  startAnalysForNewUser: boolean;
  category: string;
  isStandard: boolean;
  canCombineWith: string;
  type: string;
  grundUrval: IGrundurval | null;
  obligatoriskGas: IGBSRole[];
  obligatoriskEjGas: boolean;
}

interface IChildArgs {
  utdelningar: IRapportUtdelning[];
  urval: IGrundurval[];
}

export interface IGrundurval {
  id: string | null;
  name: string;
  standardGrundurval: boolean;
}

const LOPNR_USED_FOR_GEMENSAM_UTDELNING = 0;
const STANDARD_GRUNDURVAL_VISIBILITY = '5';

const getUrval = (
  associations: IUtdeladAssociations | null
): IGrundurval | null => {
  if (!associations || !associations.Grundurval) {
    return null;
  }

  return {
    id: associations.Grundurval.id,
    name: associations.Grundurval.NAME || '',
    standardGrundurval:
      associations.Grundurval.VISIBILITY === STANDARD_GRUNDURVAL_VISIBILITY,
  };
};

const getGrundUrval = (
  associations: IRapportAssociations | null
): IGrundurval | null => {
  if (!associations || !associations.Utdelade) {
    return null;
  }
  const urval = associations.Utdelade.find(
    u => u.LOPNR === LOPNR_USED_FOR_GEMENSAM_UTDELNING
  );
  if (urval) {
    return getUrval(urval._Associations);
  } else {
    return null;
  }
};

const getType = (associations: IRapportAssociations | null): string => {
  if (!associations || !associations.Dimensioner) {
    return '';
  }

  if (associations.Dimensioner[0]) {
    return prettifyType(associations.Dimensioner[0]._typ);
  }

  return '';
};

const prettifyType = (type: TEDEKRapportDimensionTypEnum): string => {
  switch (type) {
    case TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONLOPANDE:
      return 'Löpande';
    case TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONRADDRIVEN:
      return 'Radbaserad';
    case TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONSAMMANSATT:
      return 'Sammansatt';
    case TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONBUDGETERINGLOPANDE:
      return 'Budgetering löpande';
    case TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONBUDGETERINGSMODELL:
      return 'Budgetering modell';
    case TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONBUDGETERINGSUNDERLAG:
      return 'Budgeteringsunderlag';
  }
};

const getUtdelning = (
  rawUtdelning: AnalysutdelningQuery_db_EKEkonomirapport_All,
  gbsRoles: IGBSRole[]
): IRapportUtdelning => {
  return {
    id: rawUtdelning.id,
    name: rawUtdelning.BENAMNING,
    availableForAnalys: rawUtdelning.TILLGANGLIG_EK_ANALYS || false,
    defaultForNewUser: rawUtdelning.USER_DEFAULT_EK_ANALYS || false,
    startAnalysForNewUser: rawUtdelning.USER_START_EK_ANALYS || false,
    grundUrval: getGrundUrval(rawUtdelning._Associations),
    category: rawUtdelning.Kategori || '',
    isStandard: rawUtdelning.Standardrapport || false,
    canCombineWith: rawUtdelning.KombMedKodbeh || '',
    type: getType(rawUtdelning._Associations),
    obligatoriskEjGas: rawUtdelning.OBLIGATORISK_EJ_GAS ?? false,
    obligatoriskGas: rawUtdelning.OBLIGATORISK_GAS
      ? getGbsRoles(rawUtdelning.OBLIGATORISK_GAS, gbsRoles)
      : [],
  };
};

const getUtdelningar = (
  utdelningarRaw: AnalysutdelningQuery_db_EKEkonomirapport_All[],
  gbsRoles: IGBSRole[]
): IRapportUtdelning[] => {
  return utdelningarRaw.map(utdelning => getUtdelning(utdelning, gbsRoles));
};

const compareUtdelning = (
  a: IRapportUtdelning,
  b: IRapportUtdelning
): number => {
  return stringCompareNumeric(a.category, b.category);
};

const compareUrval = (a: IGrundurval, b: IGrundurval): number => {
  if (a.name > b.name) {
    return 1;
  } else if (a.name < b.name) {
    return -1;
  }

  return 0;
};

const getUrvalOptions = (
  rawUrval: AnalysutdelningQuery_ek_grundurval[]
): IGrundurval[] => {
  const res = rawUrval.map((u): IGrundurval => {
    return {
      id: u.id,
      name: u.NAME || 'Namn saknas',
      standardGrundurval: u.VISIBILITY === STANDARD_GRUNDURVAL_VISIBILITY,
    };
  });

  return res;
};

const AnalysutdelningQuery: React.FunctionComponent<IProps> = props => {
  const filterRapportTyp = (
    utdelningarRaw: AnalysutdelningQuery_db_EKEkonomirapport_All[]
  ): AnalysutdelningQuery_db_EKEkonomirapport_All[] => {
    return utdelningarRaw.filter(utdelning => {
      if (utdelning.EGEN_ANALYS) {
        utdelning.Kategori = 'Egna lokala';
      }
      if (
        utdelning._Associations &&
        utdelning._Associations.Dimensioner &&
        utdelning._Associations.Dimensioner[0]
      ) {
        const typ = utdelning._Associations.Dimensioner[0]._typ;
        return (
          typ === TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONLOPANDE ||
          typ === TEDEKRapportDimensionTypEnum.RAPPORTDIMENSIONRADDRIVEN
        );
      } else {
        return false;
      }
    });
  };

  const filterUtdelningar = (
    utdelningar: AnalysutdelningQuery_db_EKEkonomirapport_All[]
  ): AnalysutdelningQuery_db_EKEkonomirapport_All[] => {
    return filterRapportTyp(filterKodplanId(utdelningar));
  };

  const filterKodplanId = (
    utdelningar: AnalysutdelningQuery_db_EKEkonomirapport_All[]
  ): AnalysutdelningQuery_db_EKEkonomirapport_All[] => {
    return utdelningar.filter(
      utdelning => utdelning.KPLAN_ID === props.activeKodplan.internalId
    );
  };

  const queryResult = useHandledQuery<IData, IVariables>(query, {
    variables: { dbName: props.activeDatabaseName },
    fetchPolicy: 'network-only',
  });

  if (!queryResult.data) {
    return queryResult.renderComponent;
  }

  const data = queryResult.data;

  if (!data.db || !data.db.EKEkonomirapport_All) {
    return null;
  }

  const utdelningar: IRapportUtdelning[] = mergeSort(
    getUtdelningar(
      filterUtdelningar(data.db.EKEkonomirapport_All),
      data.gbsroles ?? []
    ),
    compareUtdelning
  );

  const urval: IGrundurval[] = data.ek_grundurval
    ? mergeSort(getUrvalOptions(data.ek_grundurval), compareUrval)
    : [];

  return props.children({ utdelningar, urval });
};

export { IGBSRole };
export default AnalysutdelningQuery;
